import SessionOverviewPageActionTypes from './session-overview-page-action-types';
import axios from 'axios';
import ValidationError from '../../../utils/validation-error';
import { ROLE_PERMISSIONS as Permissions } from '../../../resources/rolePermissions';
import { sortingToStringWithSubstitutes } from '../../../utils/objectUtils';

export const openSessionOverviewConfirmDialog = () => async(dispatch) => {
  dispatch({ type: SessionOverviewPageActionTypes.TOGGLE_SESSION_OVERVIEW_CONFIRM_DIALOG, payload: { isVisible: true } });
};

export const closeSessionOverviewConfirmDialog = () => async(dispatch) => {
  dispatch({ type: SessionOverviewPageActionTypes.TOGGLE_SESSION_OVERVIEW_CONFIRM_DIALOG, payload: { isVisible: false } });
};

export const removeSessionOverviewConfirmDialog = (payload) => async(dispatch, getState, { api }) => {
  try {
    await api.remove(`api/general_competency_events/${payload.eventId}/sessions/${payload.sessionId}`);
    dispatch({ type: SessionOverviewPageActionTypes.TOGGLE_SESSION_OVERVIEW_CONFIRM_DIALOG, payload: { isVisible: false } });
    payload.navigate(`/general_competency_events/${payload.eventId}/overview`);
  } catch (error) {
    if (error instanceof ValidationError) {
      dispatch({ type: SessionOverviewPageActionTypes.TOGGLE_SESSION_OVERVIEW_CONFIRM_DIALOG, payload: { isVisible: true, errorMessage: error.message } });
    } else {
      throw error;
    }
  }
};

export const loadSessionOverviewPage = (payload) => async(dispatch, getState, { api }) => {
  dispatch({ type: SessionOverviewPageActionTypes.LOAD_SESSION_OVERVIEW_PAGE_START });
  const state = getState().generalCompetencyEventSessionOverviewPage;
  try {
    state.cancelToken?.cancel();
    state.cancelToken = axios.CancelToken.source();
    const event = await api.get(`api/general_competency_events/${payload.eventId}`, null, state.cancelToken.token);
    const session = await api.get(`api/general_competency_events/${payload.eventId}/sessions/${payload.sessionId}`, null, state.cancelToken.token);
    if (session?.startAt) {
      session.startAt = new Date(session.startAt);
    }
    const canWrite = getState().app.userPermissions.includes(Permissions.GENERAL_COMPETENCY_EVENTS_WRITE);
    dispatch({
      type: SessionOverviewPageActionTypes.LOAD_SESSION_OVERVIEW_PAGE_END,
      payload: {
        event,
        session,
        canWrite
      }
    });
  } catch (error) {
    if (!(error instanceof axios.Cancel)) {
      dispatch({ type: SessionOverviewPageActionTypes.LOAD_SESSION_OVERVIEW_PAGE_END });
      throw error;
    }
  }
};

export const loadSessionParticipants = (payload) => async(dispatch, getState, { api }) => {
  dispatch({ type: SessionOverviewPageActionTypes.LOAD_SESSION_PARTICIPANTS_START });
  const state = getState().generalCompetencyEventSessionOverviewPage;
  const skip = payload?.skip ?? state.participantsSkip;
  const take = payload?.take ?? state.participantsTake;
  const query = {
    skip,
    limit: take,
    keyword: state.participantsKeyword || undefined,
    sorting: sortingToStringWithSubstitutes(state.participantsSorting, [
      { originalName: 'studentName', substituteName: 'Surname' },
      { originalName: 'occurredAt', substituteName: 'OccurredAt' }
    ])
  };
  try {
    const result = await api.get(`api/general_competency_events/${payload.eventId}/sessions/${payload.sessionId}/participants`, query);
    dispatch({
      type: SessionOverviewPageActionTypes.LOAD_SESSION_PARTICIPANTS_END,
      payload: {
        participants: result ? result.data?.map(participant => ({
          ...participant,
          occurredAt: participant.occurredAt ? new Date(participant.occurredAt) : null
        })) : [],
        total: result ? result.total : 0,
        skip,
        take
      }
    });
  } catch (error) {
    dispatch({ type: SessionOverviewPageActionTypes.LOAD_SESSION_PARTICIPANTS_END });
    throw error;
  }
};

export const setParticipantsKeyword = (keyword) => ({
  type: SessionOverviewPageActionTypes.SET_PARTICIPANTS_KEYWORD,
  payload: { keyword }
});

export const sortParticipants = (sorting) => ({
  type: SessionOverviewPageActionTypes.SORT_PARTICIPANTS,
  payload: { sorting }
});

export const cleanupSessionOverviewPage = () => ({
  type: SessionOverviewPageActionTypes.CLEANUP_SESSION_OVERVIEW
});

export const toggleAddParticipantDialog = (isVisible) => ({
  type: SessionOverviewPageActionTypes.TOGGLE_ADD_PARTICIPANT_DIALOG,
  payload: { isVisible }
});

export const loadAvailableStudents = (payload) => async(dispatch, getState, { api }) => {
  dispatch({ type: SessionOverviewPageActionTypes.LOAD_AVAILABLE_STUDENTS_START });
  try {
    const result = await api.get('api/users_search', { keyword: payload.keyword, type: 'Student' });
    dispatch({
      type: SessionOverviewPageActionTypes.LOAD_AVAILABLE_STUDENTS_END,
      payload: {
        students: result ? result.data?.map(user => ({
          ...user,
          text: `${user.name} ${user.surname}${user.details ? ` (${user.details.split(';').join(', ')})` : ''}`
        })) : []
      }
    });
  } catch (error) {
    dispatch({ type: SessionOverviewPageActionTypes.LOAD_AVAILABLE_STUDENTS_END });
    throw error;
  }
};

export const openRemoveParticipantDialog = (payload) => ({
  type: SessionOverviewPageActionTypes.TOGGLE_REMOVE_PARTICIPANT_DIALOG,
  payload: { participantId: payload.id }
});

export const closeRemoveParticipantDialog = () => ({
  type: SessionOverviewPageActionTypes.TOGGLE_REMOVE_PARTICIPANT_DIALOG,
  payload: null
});

export const removeParticipant = (payload) => async(dispatch, getState, { api }) => {
  const state = getState().generalCompetencyEventSessionOverviewPage;
  if (state.selectedParticipantId > 0) {
    try {
      await api.remove(`api/general_competency_events/${payload.eventId}/sessions/${payload.sessionId}/participants/${state.selectedParticipantId}`);
      dispatch({ type: SessionOverviewPageActionTypes.TOGGLE_REMOVE_PARTICIPANT_DIALOG, payload: null });
      dispatch(loadSessionParticipants({ eventId: payload.eventId, sessionId: payload.sessionId }));
    } catch (error) {
      dispatch({ type: SessionOverviewPageActionTypes.TOGGLE_REMOVE_PARTICIPANT_DIALOG, payload: null });
      throw error;
    }
  }
};

export const addParticipant = (payload) => async(dispatch, getState, { api }) => {
  try {
    await api.post(`api/general_competency_events/${payload.eventId}/sessions/${payload.sessionId}/participants/${payload.userId}`);
    dispatch({ type: SessionOverviewPageActionTypes.TOGGLE_ADD_PARTICIPANT_DIALOG, payload: { isVisible: false } });
    dispatch(loadSessionParticipants({ eventId: payload.eventId, sessionId: payload.sessionId }));
  } catch (error) {
    let validationResult = null;
    if (error instanceof ValidationError) {
      validationResult = {
        errorMessage: error.message,
        errors: error.errors
      };
    }
    dispatch({ type: SessionOverviewPageActionTypes.ADD_PARTICIPANT_END, payload: { validationResult } });
  }
};
